import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, ZoomIn, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO, { breadcrumbSchema } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Lightbox ─── */
const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  useLayoutEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  return createPortal(
    <div
      style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.95)' }}
      onClick={onClose}
    >
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs font-bold text-white bg-white bg-opacity-10 px-3 py-1.5 rounded-full">
        {index + 1} / {images.length}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 w-11 h-11 rounded-full bg-white bg-opacity-10 hover:bg-opacity-25 flex items-center justify-center text-white transition-all"
        data-testid="lightbox-prev"
      >
        <ChevronLeft size={22} />
      </button>
      <div style={{ position: "relative", display: "inline-flex", maxWidth: "90vw", maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: "absolute", top: 8, right: 8, width: 34, height: 34, backgroundColor: "rgba(50,50,50,0.92)", border: "none", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 20 }} data-testid="lightbox-close"><X size={15} style={{ color: "#fff" }} /></button>
        <img
          src={images[index]}
          alt={`Gallery ${index + 1}`}
          style={{ maxHeight: "88vh", maxWidth: "90vw", objectFit: "contain", borderRadius: 8 }}
        />
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 w-11 h-11 rounded-full bg-white bg-opacity-10 hover:bg-opacity-25 flex items-center justify-center text-white transition-all"
        data-testid="lightbox-next"
      >
        <ChevronRight size={22} />
      </button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1 max-w-xs overflow-hidden">
        {images.slice(Math.max(0, index - 5), Math.min(images.length, index + 6)).map((_, i) => {
          const realIdx = Math.max(0, index - 5) + i;
          return (
            <div
              key={realIdx}
              className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all"
              style={{ backgroundColor: realIdx === index ? '#E8520A' : 'rgba(255,255,255,0.3)' }}
            />
          );
        })}
      </div>
    </div>,
    document.body
  );
};

/* ─── Gallery tile ─── */
const GalleryTile = ({ src, index, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error) return null;

  return (
    <div
      className="relative group cursor-zoom-in overflow-hidden bg-black"
      style={{ aspectRatio: '1/1' }}
      onClick={() => onClick(index)}
      data-testid={`gallery-tile-${index}`}
    >
      {!loaded && (
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }} />
      )}
      <img
        src={src}
        alt={`Total Composites build ${index + 1}`}
        className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-108 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ transform: 'scale(1)', transition: 'transform 0.6s ease, opacity 0.3s ease' }}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition-opacity duration-300 flex items-center justify-center">
        <ZoomIn size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
    </div>
  );
};

/* ─── Main Gallery page ─── */
const Gallery = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [displayCount, setDisplayCount] = useState(48);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await fetch(`${API}/api/gallery`);
        if (res.ok) {
          const data = await res.json();
          setGalleryImages(data.map(img => img.url));
        }
      } catch (err) {
        console.error('Gallery fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const openLightbox = useCallback((i) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => setLightboxIndex((p) => (p - 1 + galleryImages.length) % galleryImages.length), [galleryImages.length]);
  const nextImage = useCallback(() => setLightboxIndex((p) => (p + 1) % galleryImages.length), [galleryImages.length]);
  
  const loadMore = () => setDisplayCount(prev => Math.min(prev + 48, galleryImages.length));
  const hasMore = displayCount < galleryImages.length;

  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000', color: '#fff' }}>
      <SEO
        title="Gallery"
        description="Browse photos of Total Composites expedition truck campers in action — overlanding builds, installations, and customer adventures from around the world."
        path="/gallery"
        keywords="truck camper gallery, expedition camper photos, overlanding builds, Total Composites installations, composite camper images"
        jsonLd={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Gallery', path: '/gallery' }
        ])}
      />
      {/* Header */}
      <section className="py-24 text-center px-6" style={{ backgroundColor: '#000' }}>
        <p className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: '#E8520A', letterSpacing: '0.2em' }}>
          {language === 'fr' ? 'Galerie d\'expédition' : 'Expedition Gallery'}
        </p>
        <h1 className="text-4xl lg:text-6xl font-black text-white uppercase mb-5" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.01em' }}>
          {language === 'fr' ? <>Ce que les aventuriers<br />créent</> : <>What Adventurers<br />Are Creating</>}
        </h1>
        <p className="max-w-xl mx-auto text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {language === 'fr' ? 'De vraies constructions par des constructeurs et aventuriers du monde entier — toutes propulsées par les systèmes de panneaux composites TOTAL COMPOSITES.' : 'Real builds from builders and adventurers around the world — all powered by TOTAL COMPOSITES composite panel systems.'}
        </p>
        {!loading && <p className="text-xs mt-3" style={{ color: 'rgba(255,255,255,0.2)' }}>{galleryImages.length} photos</p>}
        <div className="mt-8 w-px h-12 mx-auto" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }} />
      </section>

      {/* Grid */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#000' }}>
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              <RefreshCw size={20} className="animate-spin mr-3" />
              <span className="text-sm">{language === 'fr' ? 'Chargement de la galerie...' : 'Loading gallery...'}</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1" data-testid="gallery-grid">
                {galleryImages.slice(0, displayCount).map((src, i) => (
                  <GalleryTile key={`${src}-${i}`} src={src} index={i} onClick={openLightbox} />
                ))}
              </div>
              
              {hasMore && (
                <div className="text-center mt-12">
                  <button
                    onClick={loadMore}
                    className="inline-flex items-center gap-2 px-10 py-4 text-xs font-bold uppercase tracking-widest text-white border border-white hover:bg-white hover:text-black transition-all duration-300"
                    data-testid="load-more-btn"
                  >
                    {language === 'fr' ? 'CHARGER PLUS' : 'LOAD MORE'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={galleryImages}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </div>
  );
};

export default Gallery;
